import React, { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/Button";

import { DETAIL_LIST } from "../constants";
import { OperationSummary } from "./OperationList";
import RevisionSqlBlock from "./RevisionSqlBlock";

/**
 * A list of schema revisions, each with the title from its own migration file
 * and, on expansion, the operations parsed out of its code.
 *
 * Used for the revisions an upgrade would run, so it is rendered in apply order,
 * oldest first — the order the server returns them in. The numbering is that
 * order made visible: "3 of 6" tells a reader how far into the run a failure
 * happened.
 *
 * The one-line `operationSummary` sits under every title, because 43 of the 97
 * migrations in this tree have a title and no description, and for those the
 * summary is the only thing that says what the revision touches. Expanding shows
 * the individual steps.
 *
 * A revision with no title still gets a row. Dropping it would make a pending
 * list of six look like a list of five, and the one this build cannot describe
 * is the one worth looking at.
 */
const RevisionList = ({ revisions, max }) => {
    const [expanded, setExpanded] = useState(null);

    if (!revisions?.length) return null;
    const shown = max ? revisions.slice(0, max) : revisions;
    const hidden = revisions.length - shown.length;

    return (
        <div className={DETAIL_LIST}>
            {shown.map((revision, index) => {
                const isOpen = expanded === revision.revision;
                const operations = revision.upgradeOperations ?? [];
                const canExpand = operations.length > 0;
                const Chevron = isOpen ? ChevronDown : ChevronRight;

                return (
                    <div key={revision.revision} className="px-3 py-2">
                        <div className="flex items-start gap-2">
                            <span className="text-[11px] text-text-muted font-mono shrink-0 pt-0.5">
                                {index + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="text-[12px] text-text-primary">
                                    {revision.title || (
                                        <span className="text-text-muted italic">
                                            not described in this build
                                        </span>
                                    )}
                                </div>
                                {/* The shared summary, so this list and the
                                    schema drawer describe a revision the same
                                    way — including the raw-SQL caveat, which
                                    an inline copy of `operationSummary` here
                                    used to leave out. */}
                                <div className="mt-0.5">
                                    <OperationSummary revision={revision} />
                                </div>
                                {revision.isReversible === false && (
                                    <div className="text-[11px] text-tone-amber mt-0.5 flex items-start gap-1">
                                        <AlertTriangle
                                            size={11}
                                            className="shrink-0 mt-0.5"
                                            aria-hidden="true"
                                        />
                                        <span>Cannot be undone by a downgrade</span>
                                    </div>
                                )}
                                <div
                                    className="text-[11px] text-text-muted mt-0.5 font-mono truncate"
                                    title={revision.revision}
                                >
                                    {revision.revision}
                                    {revision.isMerge && " · merge"}
                                    {revision.createdAt && ` · ${revision.createdAt}`}
                                </div>
                            </div>
                            {canExpand && (
                                <Button
                                    onClick={() =>
                                        setExpanded(isOpen ? null : revision.revision)
                                    }
                                    aria-expanded={isOpen}
                                    className="shrink-0 text-[11px] text-text-secondary hover:text-text-primary flex items-center gap-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-tone-violet/60 rounded"
                                >
                                    <Chevron size={12} aria-hidden="true" />
                                    SQL
                                </Button>
                            )}
                        </div>

                        {/* Fetched on expand, not with the list: the largest
                            migration here renders 250 lines, and nobody wants
                            all of them at once. */}
                        {isOpen && (
                            <div className="mt-2 ml-5">
                                <RevisionSqlBlock
                                    revision={revision.revision}
                                    fallbackOperations={operations}
                                />
                            </div>
                        )}
                    </div>
                );
            })}
            {hidden > 0 && (
                <div className="px-3 py-2 text-[11px] text-text-secondary">
                    and {hidden} more — open the Schema column for the full list.
                </div>
            )}
        </div>
    );
};

export default RevisionList;
